import { REST_FALLBACK_SEC } from "@jackedlogs/shared";

import type { useRestPicker } from "../../Logger/useRestPicker";
import type { useRestAdvance } from "../../Logger/useRestAdvance";
import type { UseRestTimer } from "../../Logger/useRestTimer";
import type { UseHoldTimer } from "../../Logger/useHoldTimer";

/** PE-22 K2 — the NON ISTANZIA machinery, at rest.
 *
 *  The narrowed logger bags (`TimelineLoggerInput`/`TimelineFocusSectionInput`,
 *  loggerContext.ts) still NAME `restTimer`/`restPicker`/`restAdvance`/`hold`
 *  because the card reads their SHAPE — but these exist ONLY to run a live
 *  countdown a real logging session owns, and every path that would start one
 *  is mode-gated off in `prescribe` (useTimelineLogger.ts gates the rest
 *  transition on `mode !== 'log'`; useSetCyclePill never dispatches a
 *  completion in prescribe; useEffortCommit.onFail returns immediately).
 *
 *  ⛔ So each value below is the hook's OWN idle state — `running:false`,
 *  no-op callbacks — NOT `as never`, NOT a cast: a real value of the real
 *  return type, sitting still because nothing on this screen ever starts it.
 *  Module-level consts (referentially stable, dep-free) so the mount's
 *  downstream `useCallback`/`useMemo` deps never churn on them.
 *
 *  Extracted out of usePrescribeMount to keep that hook single-concern and
 *  under the soft cap (K2.1 review, consolidation + code-standards agents).
 */
export const PRESCRIBE_REST_TIMER: UseRestTimer = {
  state: {
    endsAt: null,
    remainingSec: 0,
    totalSec: 0,
    isPaused: false,
    finished: false,
    exerciseId: null,
  },
  start: () => {},
  add: () => {},
  reset: () => {},
  pause: () => {},
  resume: () => {},
};

export const PRESCRIBE_REST_PICKER: ReturnType<typeof useRestPicker> = {
  saving: false,
  visible: false,
  slug: null,
  value: REST_FALLBACK_SEC,
  saveAsGlobal: false,
  setValue: () => {},
  open: () => {},
  cancel: () => {},
  toggleSaveAsGlobal: () => {},
  confirm: () => {},
};

export const PRESCRIBE_REST_ADVANCE: ReturnType<typeof useRestAdvance> = {
  pending: null,
  flashCue: null,
  resting: false,
  freezesNavigation: false,
  arm: () => {},
  defer: () => {},
  advanceKey: 0,
};

export const PRESCRIBE_HOLD: UseHoldTimer = {
  timer: {
    state: {
      mode: "down",
      endsAt: null,
      startedAt: null,
      remainingSec: 0,
      elapsedSec: 0,
      totalSec: 0,
      displaySec: 0,
      isOvertime: false,
      atZero: false,
      isPaused: false,
      isRunning: false,
      tag: null,
    },
    start: () => {},
    add: () => {},
    reset: () => {},
    pause: () => {},
    resume: () => {},
  },
  target: null,
  intro: null,
  start: () => {},
  flipDirection: () => {},
  resolveDefaultSec: () => 0,
  cancel: () => {},
  capture: () => null,
};
